import React from 'react';

export type StepType = 'add' | 'subtract' | 'subtotal' | 'total' | 'info';

export interface CalculationStep {
  label: string;
  value: number | string;
  type?: StepType;
  sublabel?: string;
}

interface CalculationBreakdownProps {
  steps: CalculationStep[];
  formatValue?: (value: number | string) => string;
  title?: string;
}

export const CalculationBreakdown: React.FC<CalculationBreakdownProps> = ({
  steps,
  formatValue = (v) => typeof v === 'number' ? `$${v.toLocaleString()}` : v,
  title,
}) => {
  const getStepStyle = (type?: StepType) => {
    switch (type) {
      case 'subtract':
        return { label: 'text-gray-500', value: 'text-gray-500' };
      case 'subtotal':
        return { label: 'font-medium text-gray-700', value: 'font-medium text-gray-700', border: 'pt-2 border-t border-gray-200' };
      case 'total':
        return { label: 'font-semibold text-gray-900', value: 'font-semibold text-gray-900', border: 'pt-2 border-t border-gray-200' };
      case 'info':
        return { label: 'text-gray-500 text-xs', value: 'text-gray-500 text-xs' };
      default:
        return { label: 'text-gray-600', value: 'font-medium' };
    }
  };

  const formatDisplayValue = (value: number | string, type?: StepType) => {
    const formatted = formatValue(value);
    if (type === 'subtract' && typeof value === 'number' && value > 0) {
      return `(${formatted})`;
    }
    return formatted;
  };

  return (
    <div className="bg-gray-50 p-4 text-sm border border-gray-200">
      {title && <p className="font-medium text-gray-900 mb-3">{title}</p>}
      <div className="space-y-2">
        {steps.map((step, index) => {
          const style = getStepStyle(step.type);
          return (
            <div key={index} className={style.border || ''}>
              <div className="flex justify-between">
                <span className={style.label}>
                  {step.type === 'subtract' && '− '}
                  {step.type === 'add' && '+ '}
                  {step.label}
                </span>
                <span className={style.value}>
                  {formatDisplayValue(step.value, step.type)}
                </span>
              </div>
              {step.sublabel && (
                <p className="text-xs text-gray-400 mt-0.5">{step.sublabel}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
